import type { Match } from '@uefadigital/panenka';
import { LineupStatus, MatchStatus } from '@uefadigital/panenka';
import { PkDivider, PkMatchUnit } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useRef } from 'react';
import { useNavigate } from 'react-router';

import LocationObserver from 'components/LocationObserver';
import PrintOutsButton from 'components/PrintOutsButton';
import { useMatchUnitTranslations } from 'hooks/useMatchUnitTranslations';

import * as style from './style.module.css';

interface MatchUnitData {
  match: Match;
  isDesktop: boolean;
  hasDivider: boolean;
  isFixturesPage?: boolean;
  hideMatchUnitDetail: boolean;
}

enum MatchUnitSize {
  HEADER = 'header',
  XL = 'xl',
  L = 'l',
  M = 'm',
  S = 's',
}

export const MatchUnit = ({
  match,
  isDesktop,
  hasDivider,
  isFixturesPage = true,
  hideMatchUnitDetail = false,
}: MatchUnitData) => {
  const navigate = useNavigate();
  const muTranslations = useMatchUnitTranslations();
  const printOutsButtonWrapper = useRef<HTMLDivElement>(null!);
  const redirectLink = (e: MouseEvent<HTMLDivElement>) => {
    const currentLocation = location.pathname.replace(/\/match.*$/, '');
    const link = `${currentLocation}/match/${match.id}`.replace(/\/{2,}/g, '/');

    if (
      (printOutsButtonWrapper.current && e && !printOutsButtonWrapper.current?.contains(e.target as Element)) ||
      !isFixturesPage
    ) {
      navigate(link);
    }
  };

  if (!isFixturesPage) {
    return (
      <PkMatchUnit
        matchData={match}
        size={MatchUnitSize.M}
        {...muTranslations}
        className={`${style.selectorBackground} ${hideMatchUnitDetail ? '' : style.pointer}`}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        onClick={redirectLink}
      />
    );
  }

  return (
    <div
      className={clsx(
        !hideMatchUnitDetail && style.pointer,
        'pk-h--100 pk-text--wrap pk-d--flex pk-flex--column pk-justify-content--center pk-position--relative',
      )}
      onClick={(e) => {
        hideMatchUnitDetail ? e.stopPropagation() : redirectLink(e);
      }}
    >
      <PkMatchUnit matchData={match} {...muTranslations} size={isDesktop ? MatchUnitSize.XL : MatchUnitSize.M} />

      <div className="pk-position--absolute pk-right--0 pk-mr--xl pk-top--0 pk-mt--xl pk-spacing-l">
        <LocationObserver>
          <div ref={printOutsButtonWrapper}>
            {(match.status === MatchStatus.FINISHED ||
              match.status === MatchStatus.LIVE ||
              match.lineupStatus === LineupStatus.TACTICAL_AVAILABLE) && <PrintOutsButton matchData={match} />}
          </div>
        </LocationObserver>
      </div>
      {hasDivider && <PkDivider class="divider--horizontal" direction="horizontal" />}
    </div>
  );
};
